import moment from "jalali-moment";
import { CHALLENGE_WEEK_DAY_OPTIONS } from "../../../../constants/challenge";
import {
  isoToJalaliParts,
  jalaliPartsToDate,
} from "../../../../utils/jalaliDate";
import type {
  CreateChainDayBody,
  CreateGoldenChallengeChainBody,
  GoldenChallengeChain,
  UpdateGoldenChallengeChainBody,
} from "../../../../services/webServices/challenge/types";
import { GOLDEN_WEEK_DAY, fromBackendWeekDay, toBackendWeekDay, type ChainDateRange, type ChainDayState, type ChainFormState } from "./wizardTypes";

export const createInitialChainDays = (): ChainDayState[] =>
  CHALLENGE_WEEK_DAY_OPTIONS.map((day) => ({
    weekDay: Number(day.value),
    label: day.label,
    isGolden: Number(day.value) === GOLDEN_WEEK_DAY,
    suggestions: [],
    suggestionsLoading: false,
    suggestionOffset: 0,
    selectedSuggestionId: null,
  }));

export const createInitialChainFormState = (): ChainFormState => ({
  lesson: "",
  grade: "",
  activeWeekDay: 0,
  days: createInitialChainDays(),
  dateRange: null,
});

export const formatChainDateRange = (range: ChainDateRange | null): string => {
  if (!range) return "";

  const from = moment(`${range.from.year}/${range.from.month}/${range.from.day}`, "jYYYY/jM/jD").locale("fa");
  const to = moment(`${range.to.year}/${range.to.month}/${range.to.day}`, "jYYYY/jM/jD").locale("fa");

  if (!from.isValid() || !to.isValid()) return "";

  return `${from.format("jD jMMMM")} تا ${to.format("jD jMMMM")}`;
};

export const isChainDayComplete = (day: ChainDayState): boolean => Boolean(day.selectedSuggestionId);

export const getSelectedSuggestion = (day: ChainDayState) =>
  day.suggestions.find((suggestion) => suggestion.id === day.selectedSuggestionId) ?? null;

const buildChainDays = (
  state: ChainFormState,
  weekDay?: number,
): CreateChainDayBody[] =>
  state.days
    .filter((day) => weekDay === undefined || day.weekDay === weekDay)
    .map((day) => ({ day, suggestion: getSelectedSuggestion(day) }))
    .filter((entry): entry is { day: ChainDayState; suggestion: NonNullable<ReturnType<typeof getSelectedSuggestion>> } =>
      Boolean(entry.suggestion),
    )
    .map(({ day, suggestion }) => ({
      week_day: toBackendWeekDay(day.weekDay),
      challenge_id: Number(suggestion.id),
      is_golden: day.isGolden,
    }));

export const buildChainSubmissionBody = (
  state: ChainFormState,
  status: "draft" | "active",
): CreateGoldenChallengeChainBody | null => {
  if (!state.dateRange) return null;

  const days = buildChainDays(state);
  if (days.length === 0) return null;

  return {
    grade: [Number(state.grade)].filter((grade) => !Number.isNaN(grade)),
    lesson: state.lesson,
    from_date: jalaliPartsToDate(state.dateRange.from),
    to_date: jalaliPartsToDate(state.dateRange.to),
    is_active: status === "active",
    days,
  };
};

export const mapChainToFormState = (
  chain: GoldenChallengeChain,
): ChainFormState => {
  const from = isoToJalaliParts(chain.from_date);
  const to = isoToJalaliParts(chain.to_date) ?? from;

  const days = createInitialChainDays().map((day) => {
    const chainDay = chain.days?.find(
      (entry) => fromBackendWeekDay(entry.week_day) === day.weekDay,
    );

    if (!chainDay) return day;

    const embedded = chainDay.challenge;
    const challengeId = String(chainDay.challenge_id ?? embedded?.id);
    const content = embedded?.content ?? "";
    const options = embedded?.options ?? [];

    return {
      ...day,
      isGolden: chainDay.is_golden ?? day.isGolden,
      suggestions: content
        ? [
            {
              id: challengeId,
              content,
              options,
            },
          ]
        : [],
      suggestionOffset: 0,
      suggestionsLoading: false,
      selectedSuggestionId: content ? challengeId : null,
    };
  });

  const activeDay = days.find((day) => day.selectedSuggestionId) ?? days[0];

  return {
    lesson: chain.lesson ?? "",
    grade: chain.grade?.length ? String(chain.grade[0]) : "",
    activeWeekDay: activeDay.weekDay,
    days,
    dateRange: from && to ? { from, to } : null,
  };
};

export const buildChallengeUpdateBody = (
  state: ChainFormState,
  status?: "draft" | "active",
  weekDay?: number,
): UpdateGoldenChallengeChainBody | null => {
  if (!state.dateRange) return null;

  const days = buildChainDays(state, weekDay);
  if (days.length === 0) return null;

  return {
    grade: [Number(state.grade)].filter((grade) => !Number.isNaN(grade)),
    lesson: state.lesson,
    from_date: jalaliPartsToDate(state.dateRange.from),
    to_date: jalaliPartsToDate(state.dateRange.to),
    is_active: status ? status === "active" : undefined,
    days,
  };
};
